import { useState, useEffect, useRef } from "react";
import { sendOwnerChat, saveChatSession } from "./lib/supabase";
import COLORS from "./config/colors";

const QUICK_ACTIONS = [
  { label: "What's my AR?", prompt: "What's my outstanding accounts receivable? List all unpaid invoices." },
  { label: "This week's leads", prompt: "Show me all leads that came in this week with their details." },
  { label: "Project budgets", prompt: "Give me a summary of all active project budgets — bid vs spent for each." },
  { label: "Pricing check", prompt: "Show me my current pricing model — which materials have actual cost data vs defaults?" },
];

/**
 * Simple Markdown-like renderer: bold, line breaks, bullet lists.
 */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} style={{ margin: "8px 0", paddingLeft: 20 }}>
          {listItems.map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{formatInline(item)}</li>)}
        </ul>
      );
      listItems = [];
    }
  };

  const formatInline = (line) => {
    // Bold: **text**
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Bullet list
    if (line.match(/^[\s]*[-*]\s/)) {
      listItems.push(line.replace(/^[\s]*[-*]\s/, ""));
      continue;
    }

    flushList();

    // Headers
    if (line.startsWith("### ")) {
      elements.push(<h4 key={i} style={{ fontWeight: 700, fontSize: 14, margin: "12px 0 4px", color: COLORS.bark }}>{line.slice(4)}</h4>);
    } else if (line.startsWith("## ")) {
      elements.push(<h3 key={i} style={{ fontWeight: 700, fontSize: 15, margin: "12px 0 4px", color: COLORS.bark }}>{line.slice(3)}</h3>);
    } else if (line.startsWith("# ")) {
      elements.push(<h2 key={i} style={{ fontWeight: 700, fontSize: 16, margin: "12px 0 4px", color: COLORS.bark }}>{line.slice(2)}</h2>);
    } else if (line.trim() === "") {
      elements.push(<br key={i} />);
    } else if (line.startsWith("---") || line.startsWith("===") || line.startsWith("═══")) {
      elements.push(<hr key={i} style={{ border: "none", borderTop: "1px solid rgba(155,142,130,0.2)", margin: "8px 0" }} />);
    } else {
      elements.push(<p key={i} style={{ margin: "4px 0" }}>{formatInline(line)}</p>);
    }
  }

  flushList();
  return elements;
}

export default function OwnerChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hey Chris. What do you need? I've got access to your projects, invoices, receipts, leads, and pricing model. Ask me anything or use a quick action below." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    if (!text?.trim()) return;
    const userMsg = text.trim();
    setInput("");
    const updated = [...messages, { role: "user", text: userMsg }];
    setMessages(updated);
    setLoading(true);

    const apiMessages = updated
      .filter(m => m.role !== "system")
      .map(m => ({ role: m.role, text: m.text }));

    const response = await sendOwnerChat(apiMessages);

    if (response) {
      const withResponse = [...updated, { role: "assistant", text: response }];
      setMessages(withResponse);

      // Save session
      saveChatSession({
        id: sessionId,
        sessionType: "owner",
        messages: withResponse,
      }).then(saved => {
        if (saved?.id && !sessionId) setSessionId(saved.id);
      });
    } else {
      setMessages(prev => [...prev, {
        role: "assistant",
        text: "I couldn't reach the AI service. Make sure the owner-chat edge function is deployed and ANTHROPIC_API_KEY is set. In the meantime, you can check the admin panel for your data."
      }]);
    }

    setLoading(false);
  };

  const styles = {
    container: { display: "flex", flexDirection: "column", height: "calc(100vh - 180px)", minHeight: 500, maxWidth: 900, margin: "0 auto" },
    header: { padding: "16px 20px", background: COLORS.bark, borderRadius: "16px 16px 0 0", color: COLORS.snow, display: "flex", alignItems: "center", gap: 12 },
    messages: { flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16, background: COLORS.white, borderLeft: `1px solid rgba(155,142,130,0.12)`, borderRight: `1px solid rgba(155,142,130,0.12)` },
    inputArea: { padding: 16, background: COLORS.white, borderRadius: "0 0 16px 16px", borderTop: `1px solid rgba(155,142,130,0.12)`, border: `1px solid rgba(155,142,130,0.12)` },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(196,145,59,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
          {"\u{1F9E0}"}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, fontFamily: "'Playfair Display', serif" }}>Owner AI Assistant</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>Full database access &middot; Projects, invoices, receipts, leads, pricing</div>
        </div>
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: msg.role === "assistant" ? "90%" : "75%",
              padding: "12px 16px",
              borderRadius: 14,
              fontSize: 13.5,
              lineHeight: 1.6,
              background: msg.role === "user" ? COLORS.bark : COLORS.cream,
              color: msg.role === "user" ? COLORS.snow : COLORS.bark,
              borderBottomRightRadius: msg.role === "user" ? 4 : 14,
              borderBottomLeftRadius: msg.role === "user" ? 14 : 4,
            }}>
              {msg.role === "assistant" ? renderMarkdown(msg.text) : msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 4, padding: "10px 16px" }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS.stone, opacity: 0.4, animation: `pulse 1s ease-in-out ${i * 0.15}s infinite` }} />
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick Actions */}
      {messages.length <= 2 && (
        <div style={{ display: "flex", gap: 8, padding: "12px 16px", background: COLORS.white, borderLeft: `1px solid rgba(155,142,130,0.12)`, borderRight: `1px solid rgba(155,142,130,0.12)`, flexWrap: "wrap" }}>
          {QUICK_ACTIONS.map(action => (
            <button
              key={action.label}
              onClick={() => send(action.prompt)}
              disabled={loading}
              style={{
                padding: "8px 14px", borderRadius: 8,
                border: `1px solid rgba(196,145,59,0.3)`, background: "rgba(196,145,59,0.06)",
                color: COLORS.bark, fontSize: 12, fontWeight: 600, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={styles.inputArea}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !loading && send(input)}
            placeholder="Draft an estimate for the Park job... How's the Mitchell budget?... What's my AR?"
            disabled={loading}
            style={{
              flex: 1, padding: "12px 16px", borderRadius: 10,
              border: "1px solid rgba(155,142,130,0.3)", fontSize: 14,
              fontFamily: "'DM Sans', sans-serif", outline: "none", background: COLORS.cream,
            }}
          />
          <button
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            style={{
              padding: "12px 24px", borderRadius: 10, border: "none",
              background: loading ? COLORS.stone : COLORS.bark, color: COLORS.honey,
              fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {loading ? "..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
